import { useState } from "react";
import { Alert, Button, Col, FormControl, FormLabel, FormSelect, Row } from "react-bootstrap";
import { IAIPageCreationBrief } from "./types";
import { AddUpdatePageCommand } from "../models/content/AddUpdatePageCommand";
import styles from "./NewPageWizard.module.scss";

interface INewVersionWizard {
  pageDetails: AddUpdatePageCommand;
  aiAvailable: boolean;
  onCancel: () => void;
  onManualVersionCreated: () => Promise<void> | void;
  onAIVersionCreated: (brief: IAIPageCreationBrief) => Promise<void> | void;
}

const defaultBrief: IAIPageCreationBrief = {
  pageKind: "landing-page",
  primaryGoal: "",
  targetAudience: "",
  tone: "confident and practical",
  keySections: "",
  contentInstructions: ""
};

const steps = [
  "Choose path",
  "AI brief"
];

const NewVersionWizard = ({ pageDetails, aiAvailable, onCancel, onManualVersionCreated, onAIVersionCreated }: INewVersionWizard): JSX.Element => {
  const [creationMode, setCreationMode] = useState<"manual" | "ai" | null>(null);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [brief, setBrief] = useState<IAIPageCreationBrief>(defaultBrief);
  const [showBriefValidation, setShowBriefValidation] = useState<boolean>(false);

  const validateBrief = (): boolean => {
    const valid = brief.pageKind.trim().length > 0 && brief.contentInstructions.trim().length > 0;
    setShowBriefValidation(!valid);
    return valid;
  };

  const updateBrief = (name: keyof IAIPageCreationBrief, value: string): void => {
    setBrief((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContinue = async (): Promise<void> => {
    if (stepIndex === 0) {
      if (creationMode === "manual") {
        setIsSaving(true);
        try {
          await onManualVersionCreated();
        } finally {
          setIsSaving(false);
        }
      }

      if (creationMode === "ai") {
        setStepIndex(1);
      }
      return;
    }

    if (!validateBrief()) {
      return;
    }

    setIsSaving(true);
    try {
      await onAIVersionCreated(brief);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = (): void => {
    if (stepIndex === 0) {
      onCancel();
      return;
    }

    setStepIndex(0);
  };

  return (
    <div className={styles.wizard}>
      <div className={styles.stepper}>
        {steps.slice(0, stepIndex === 0 ? 1 : 2).map((step, index) => (
          <div key={step} className={`${styles.step} ${stepIndex === index ? styles.stepActive : ""}`.trim()}>
            <span className={styles.stepNumber}>{index + 1}</span>
            <span>{step}</span>
          </div>
        ))}
      </div>

      {stepIndex === 0 && (
        <div className={styles.panel}>
          <p className={styles.intro}>
            Start content from the version layer so page structure stays separate from authoring. This page already exists at{" "}
            <code>{pageDetails.Url || "/"}</code>, so the next step is choosing how the initial version should be created.
          </p>

          <div className={styles.choices}>
            <button
              type="button"
              className={`${styles.choiceCard} ${creationMode === "manual" ? styles.choiceSelected : ""}`.trim()}
              onClick={() => setCreationMode("manual")}>
              <h3 className={styles.choiceTitle}>Manual Version</h3>
              <p className={styles.choiceText}>
                Create the initial draft version now and continue into the standard designer flow.
              </p>
              <ul className={styles.choiceList}>
                <li>Best for direct editing</li>
                <li>No AI brief required</li>
                <li>Uses the current version workflow exactly as before</li>
              </ul>
            </button>

            {aiAvailable && (
              <button
                type="button"
                className={`${styles.choiceCard} ${creationMode === "ai" ? styles.choiceSelected : ""}`.trim()}
                onClick={() => setCreationMode("ai")}>
                <h3 className={styles.choiceTitle}>AI Content Generation</h3>
                <p className={styles.choiceText}>
                  Create the draft version, then hand it off with a focused brief so AI can propose sections, components, and content.
                </p>
                <ul className={styles.choiceList}>
                  <li>Uses the real page and version IDs</li>
                  <li>Focuses on content and layout only</li>
                  <li>Cleaner fit for the CMS model</li>
                </ul>
              </button>
            )}
          </div>
        </div>
      )}

      {stepIndex === 1 && (
        <div className={styles.panel}>
          <div className={styles.summary}>
            <h3 className={styles.summaryTitle}>Version context</h3>
            <p className={styles.summaryText}>
              The AI will work from the existing page shell for <strong>{pageDetails.Title || "Untitled page"}</strong> at{" "}
              <code>{pageDetails.Url || "/"}</code>. That keeps language, URL, and parent page structure out of the AI step.
            </p>
          </div>

          {showBriefValidation && (
            <Alert variant="warning" className="mb-0">
              Add at least the page type and the main brief before creating the AI version.
            </Alert>
          )}

          <Row className="g-3">
            <Col md={6}>
              <FormLabel>Page type</FormLabel>
              <FormSelect value={brief.pageKind} onChange={(event) => updateBrief("pageKind", event.target.value)}>
                <option value="landing-page">Landing page</option>
                <option value="service-page">Service page</option>
                <option value="product-page">Product page</option>
                <option value="about-page">About page</option>
                <option value="campaign-page">Campaign page</option>
                <option value="contact-page">Contact page</option>
                <option value="custom-page">Custom page</option>
              </FormSelect>
            </Col>
            <Col md={6}>
              <FormLabel>Primary goal</FormLabel>
              <FormControl
                type="text"
                value={brief.primaryGoal}
                placeholder="Book a demo, generate leads, explain a service..."
                onChange={(event) => updateBrief("primaryGoal", event.target.value)}
              />
            </Col>
            <Col md={6}>
              <FormLabel>Target audience</FormLabel>
              <FormControl
                type="text"
                value={brief.targetAudience}
                placeholder="Editors, site owners, agencies..."
                onChange={(event) => updateBrief("targetAudience", event.target.value)}
              />
            </Col>
            <Col md={6}>
              <FormLabel>Tone of voice</FormLabel>
              <FormControl
                type="text"
                value={brief.tone}
                placeholder="Confident, practical, premium..."
                onChange={(event) => updateBrief("tone", event.target.value)}
              />
            </Col>
            <Col xs={12}>
              <FormLabel>Required sections</FormLabel>
              <FormControl
                as="textarea"
                rows={3}
                value={brief.keySections}
                placeholder="Hero, feature grid, comparison, CTA..."
                onChange={(event) => updateBrief("keySections", event.target.value)}
              />
            </Col>
            <Col xs={12}>
              <FormLabel>Main content and design brief</FormLabel>
              <FormControl
                as="textarea"
                rows={6}
                value={brief.contentInstructions}
                placeholder="Describe the page purpose, key messages, design direction, section priorities, proof points, CTA direction, brand cues, and anything the AI should avoid. This is the main brief the AI will work from."
                onChange={(event) => updateBrief("contentInstructions", event.target.value)}
              />
              <div className="form-text">
                This is the primary brief. Use it for the richest guidance about what the page should say and how it should feel.
              </div>
            </Col>
          </Row>
        </div>
      )}

      <div className={styles.footer}>
        <p className={styles.hint}>
          Manual versions stay in the existing editor flow. AI versions create the draft first, then use the main brief plus generated rules to build the version content.
        </p>
        <div className={styles.footerActions}>
          <Button type="button" variant="outline-secondary" onClick={handleBack} disabled={isSaving}>
            {stepIndex === 0 ? "Cancel" : "Back"}
          </Button>
          <Button
            type="button"
            variant="success"
            onClick={handleContinue}
            disabled={isSaving || (stepIndex === 0 && creationMode === null)}>
            {stepIndex === 0
              ? creationMode === "manual"
                ? "Create Manual Version"
                : "Continue to AI Brief"
              : "Create AI Version"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewVersionWizard;
